import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

// Actions
import { actions as commentsActions } from 'modules/comments/actions'

// Components
import { Avatar } from 'kit'
import { Link, Button } from '@tracklib/kit'
import AddComment from 'components/comments/addcomment'
import UserBadges from 'components/profile/userbadges'

// Hooks
import { useProfile } from 'modules/profile/hooks'
import { useRequest } from 'modules/requests/hooks'
import { useIsAuthenticated } from 'modules/auth/hooks'

// Types
import { Comment, State } from 'types'

// Utils
import { getAdminHost } from '@constants'
import formatDate from 'lib/formatdate'

import styles from './index.css'

type CommentItemProps = {
  comment: Comment
  isResponse?: boolean
}

const CommentItem = ({
  isResponse,
  comment: { author, id, deleted, created_at, edited_at, text, object_id },
}: CommentItemProps): JSX.Element => {
  const dispatch = useDispatch()
  const profile = useProfile()
  const isAuthenticated = useIsAuthenticated()
  const isAuthor = profile.user === author.id
  const isStaff = profile ? profile.is_staff : false
  const commentsList = useSelector<State, Array<Comment>>(state => Object.values(state.entities.comments))
  const responses = commentsList
    .filter(c => c.parent === id)
    .sort((a: Comment, b: Comment) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .reverse()
  const postCommentRequest = useRequest('POST_COMMENT')
  const editCommentRequest = useRequest('EDIT_COMMENT', id)

  const [isEditing, setIsEditing] = useState(false)
  const [isCommenting, setIsCommenting] = useState(false)
  const [editText, setEditText] = useState('')
  const [commentText, setCommentText] = useState('')
  const [limitResponses, setLimitResponses] = useState<number>(0)

  const handleEditStart = () => {
    setIsEditing(true)
    setEditText(editText || text)
  }

  const handleEditDone = () => {
    setIsEditing(false)
    setEditText('')
  }

  const handleEditSave = () => {
    dispatch(commentsActions.editComment(id, editText)).then(() => {
      handleEditDone()
    })
  }

  const handleCommentStart = () => {
    setIsCommenting(true)
  }

  const handleCommentDone = () => {
    const postCommentPromise = dispatch(commentsActions.postComment('cms.articlepage', object_id, commentText, id))
    if (postCommentPromise) {
      postCommentPromise.then(() => {
        setIsCommenting(false)
        setCommentText('')
      })
    }
  }

  const handleUpdateCommentText = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setCommentText(value)
  }

  const handleUpdateEditText = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setEditText(value)
  }

  const handleDeleteComment = () => {
    if (confirm('Delete comment?')) {
      dispatch(commentsActions.deleteComment(id))
    }
  }

  const handleFlagComment = () => {
    if (confirm('Flag comment for moderation?')) {
      dispatch(commentsActions.flagComment(id)).then(() => alert('Thanks for your feedback!'))
    }
  }
  const handleIncreaseResponseLimit = () => setLimitResponses(limitResponses + 10)
  const canEdit = isAuthor
  const hasCommentLimit = !isResponse && limitResponses > 0 && limitResponses < responses.length
  const hiddenCount = responses.length - limitResponses
  const selectedResponses = hasCommentLimit ? responses.slice(responses.length - limitResponses, responses.length) : responses

  return (
    <li className={styles.comment}>
      <section>
        <Avatar image={author.image} id={author.id} disabled={deleted} anonymize={deleted} />
      </section>
      <section className={styles.main}>
        <header>
          {deleted ? (
            <span className={styles.author}>-</span>
          ) : (
            <Link to={`/user/${author.id}`} className={styles.author}>
              {author.name}
              <UserBadges size="small" groups={author.groups} />
            </Link>
          )}
          <span className={styles.time}>
            <time dateTime={created_at}>{formatDate(created_at)}</time>
            {edited_at && <time dateTime={edited_at}> (Edited {formatDate(edited_at, 'fromNow')})</time>}
          </span>
        </header>
        {isEditing ? (
          <AddComment
            isEdit
            isCommenting
            isLoading={postCommentRequest.isLoading || editCommentRequest.isLoading}
            commentText={editText}
            handleUpdateText={handleUpdateEditText}
            handleCommentStart={handleEditStart}
            onCommentDone={handleEditSave}
            handleCommentCancel={handleEditDone}
          />
        ) : (
          <div>
            {deleted ? (
              <span className={styles.body}>[ The user has deleted this comment. ]</span>
            ) : (
              <section>
                <p className={styles.body}>{text}</p>

                <div className={styles.actions}>
                  {!isResponse && <Button onClick={handleCommentStart}>Reply</Button>}
                  {canEdit && <Button onClick={handleEditStart}>Edit</Button>}
                  {canEdit && <Button onClick={handleDeleteComment}>Delete</Button>}
                  {isAuthenticated && !isAuthor && <Button onClick={handleFlagComment}>Flag</Button>}
                  {isStaff && <Button href={`${getAdminHost()}/comments/comment/${id}/`}>Moderate</Button>}
                </div>
              </section>
            )}
          </div>
        )}
        {hasCommentLimit && (
          <Button className={styles.showAllReplies} onClick={handleIncreaseResponseLimit}>
            {' '}
            view earlier responses <span className={styles.hiddenCount}>({hiddenCount})</span>
          </Button>
        )}
        {!isResponse && selectedResponses.length > 0 && (
          <ul className={styles.responses}>
            {selectedResponses.map(r => {
              return <CommentItem comment={r} key={r.id} isResponse />
            })}
          </ul>
        )}
        {!isResponse && (isCommenting || (selectedResponses && selectedResponses.length > 0)) && (
          <AddComment
            isReply
            isCommenting={isCommenting}
            isLoading={postCommentRequest.isLoading || editCommentRequest.isLoading}
            commentText={commentText}
            handleUpdateText={handleUpdateCommentText}
            handleCommentStart={handleCommentStart}
            onCommentDone={handleCommentDone}
          />
        )}
      </section>
    </li>
  )
}

export default CommentItem
